import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Landmark } from "lucide-react";
import { toast } from "sonner";

import SEO from "@/components/shared/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cleanerApi, getCleanerToken, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * Landing page for both Stripe Express redirects:
 *   /cleaner/stripe/complete → return_url  (verify payouts_enabled)
 *   /cleaner/stripe/refresh  → refresh_url (the link expired → mint a new one)
 */
export default function CleanerStripeReturnPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const L = (en, es) => (locale === "es" ? es : en);
  const isRefresh = pathname.endsWith("/refresh");

  const [state, setState] = useState("loading"); // loading | ready | incomplete | error
  const [redirecting, setRedirecting] = useState(false);

  const startOnboarding = useCallback(async () => {
    setRedirecting(true);
    try {
      const r = await cleanerApi.post("/cleaner/stripe/onboarding-link");
      const url = unwrap(r)?.onboarding_url;
      if (!url) throw new Error("no url");
      window.location.href = url;
    } catch (e) {
      setRedirecting(false);
      setState("error");
      toast.error(e?.response?.data?.detail?.message || L("Could not open the Stripe setup", "No pudimos abrir la configuración de Stripe"));
    }
  }, [L]);

  useEffect(() => {
    if (!getCleanerToken()) { navigate("/cleaner/login"); return; }
    if (isRefresh) { startOnboarding(); return; }
    (async () => {
      try {
        const r = await cleanerApi.post("/cleaner/stripe/sync");
        const d = unwrap(r);
        if (d?.payouts_enabled) {
          setState("ready");
          toast.success(L("Bank account connected", "Cuenta bancaria conectada"));
        } else {
          setState("incomplete");
        }
      } catch {
        setState("error");
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isRefresh]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cleaner-stripe-return-page">
      <SEO title={L("Payout setup — Ever Clean Home", "Configuración de pagos — Ever Clean Home")} noindex />
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center gap-2">
          <img src="/brand/logo-everclean-wordmark.png" alt="Ever Clean Home" className="h-11 md:h-14 w-auto object-contain" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        {(state === "loading" || redirecting) && (
          <Card className="rounded-2xl p-10 border border-[#E5E7EB] text-center" data-testid="stripe-return-loading">
            <Loader2 className="w-7 h-7 animate-spin text-[#0EA5A4] mx-auto" />
            <p className="text-sm text-[#4B5563] mt-4">
              {isRefresh
                ? L("Reopening the secure Stripe setup…", "Reabriendo la configuración segura de Stripe…")
                : L("Checking your payout setup with Stripe…", "Verificando tu configuración de pagos con Stripe…")}
            </p>
          </Card>
        )}

        {state === "ready" && !redirecting && (
          <Card className="rounded-2xl p-10 border border-emerald-200 bg-emerald-50/50 text-center" data-testid="stripe-return-ready">
            <span className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </span>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-5">
              {L("Bank account connected", "Cuenta bancaria conectada")}
            </h1>
            <p className="text-[#4B5563] mt-3 max-w-md mx-auto">
              {L("You're all set. From now on, your share of every completed job is sent to your bank automatically.",
                 "Todo listo. Desde ahora, tu parte de cada trabajo completado se envía a tu banco automáticamente.")}
            </p>
            <Button onClick={() => navigate("/cleaner/dashboard")} className="rounded-xl mt-7 bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="stripe-return-dashboard-btn">
              {L("Go to my dashboard", "Ir a mi panel")}
            </Button>
          </Card>
        )}

        {state === "incomplete" && !redirecting && (
          <Card className="rounded-2xl p-10 border border-amber-300 bg-amber-50/60 text-center" data-testid="stripe-return-incomplete">
            <span className="w-14 h-14 rounded-2xl bg-amber-200 text-amber-800 grid place-items-center mx-auto">
              <AlertTriangle className="w-7 h-7" />
            </span>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-5">
              {L("Setup incomplete", "Configuración incompleta")}
            </h1>
            <p className="text-[#4B5563] mt-3 max-w-md mx-auto">
              {L("Stripe still needs a few details before it can pay you. It only takes a minute — click below to finish.",
                 "Stripe todavía necesita algunos datos para poder pagarte. Toma un minuto — haz clic abajo para terminar.")}
            </p>
            <Button onClick={startOnboarding} className="rounded-xl mt-7 bg-amber-600 hover:bg-amber-700 text-white" data-testid="stripe-return-finish-btn">
              <Landmark className="w-4 h-4 mr-2" /> {L("Click to finish", "Haz clic para terminar")}
            </Button>
            <div className="mt-4">
              <button onClick={() => navigate("/cleaner/dashboard")} className="text-sm text-[#4B5563] hover:text-[#0EA5A4] underline" data-testid="stripe-return-skip-link">
                {L("I'll do it later", "Lo hago más tarde")}
              </button>
            </div>
          </Card>
        )}

        {state === "error" && !redirecting && (
          <Card className="rounded-2xl p-10 border border-rose-200 bg-rose-50/50 text-center" data-testid="stripe-return-error">
            <h1 className="font-display text-2xl font-extrabold tracking-tighter">
              {L("We couldn't verify your setup", "No pudimos verificar tu configuración")}
            </h1>
            <p className="text-[#4B5563] mt-3">
              {L("Please try again from your dashboard, or contact support if it keeps happening.",
                 "Intenta de nuevo desde tu panel, o contacta a soporte si sigue pasando.")}
            </p>
            <Button onClick={() => navigate("/cleaner/dashboard")} variant="outline" className="rounded-xl mt-6" data-testid="stripe-return-error-btn">
              {L("Back to dashboard", "Volver al panel")}
            </Button>
          </Card>
        )}
      </main>
    </div>
  );
}
